// Client-side "has this password turned up in a breach?" check.
//
// The four composition rules in `password-rules.ts` can only judge a password's
// *shape* (length, mixed case, digit, symbol). Supabase Auth additionally
// rejects passwords found in the HaveIBeenPwned (HIBP) breach corpus with
// "Password is known to be weak and easy to guess" — a check the client can't
// replicate from composition alone, so a rule-valid password like `Password1!`
// lights every green tick and then gets bounced server-side. This module closes
// that gap by asking HIBP directly as the user types, so the checklist catches
// a breached password *before* submit.
//
// Privacy: we use HIBP's k-anonymity range API — only the first 5 chars of the
// SHA-1 hash leave the device; the remaining suffix is matched locally. The
// password itself, and its full hash, never go over the wire. No API key needed.
// `Add-Padding` asks HIBP to pad the response so its size can't leak whether a
// match exists. (The host is allow-listed in the `connect-src` CSP.)

import { useEffect, useState } from 'react';
import { evaluatePassword } from './password-rules';

export type PwnedStatus = 'idle' | 'checking' | 'safe' | 'pwned' | 'error';

async function sha1HexUpper(input: string): Promise<string> {
  const data = new TextEncoder().encode(input);
  const digest = await crypto.subtle.digest('SHA-1', data);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('')
    .toUpperCase();
}

/**
 * Returns how many times `password` appears in the HIBP breach corpus
 * (0 = not found). Throws on network/HTTP failure or abort so the caller can
 * decide how to degrade — see `usePwnedPassword`, which fails open.
 */
export async function pwnedCount(
  password: string,
  signal?: AbortSignal,
): Promise<number> {
  const hash = await sha1HexUpper(password);
  const prefix = hash.slice(0, 5);
  const suffix = hash.slice(5);

  const res = await fetch(`https://api.pwnedpasswords.com/range/${prefix}`, {
    signal,
    headers: { 'Add-Padding': 'true' },
  });
  if (!res.ok) throw new Error(`HIBP responded ${res.status}`);

  const body = await res.text();
  for (const line of body.split('\n')) {
    const idx = line.indexOf(':');
    if (idx < 0) continue;
    if (line.slice(0, idx).trim() === suffix) {
      return parseInt(line.slice(idx + 1).trim(), 10) || 0;
    }
  }
  return 0;
}

const DEBOUNCE_MS = 400;
const TIMEOUT_MS = 7000;

/**
 * Debounced breach check for the live password checklist. Only fires once the
 * composition rules already pass (no point looking up a half-typed password).
 * Fails **open**: a HIBP outage resolves to `error`, which callers treat as
 * non-blocking — Supabase still enforces the same check server-side as the real
 * backstop, so a third-party outage must never wall off signup.
 */
export function usePwnedPassword(password: string): PwnedStatus {
  const ready = evaluatePassword(password).allMet;

  // We store only *completed* lookups, keyed by the exact password they ran for,
  // and derive `idle`/`checking` during render. That keeps every setState inside
  // an async callback (never synchronously in the effect body) and means a stale
  // result for a previous password naturally reads as `checking` until the new
  // lookup lands.
  const [outcome, setOutcome] = useState<{ pw: string; status: 'safe' | 'pwned' | 'error' } | null>(null);

  useEffect(() => {
    if (!ready) return;

    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), TIMEOUT_MS);

    const debounce = setTimeout(() => {
      pwnedCount(password, controller.signal)
        .then((count) => setOutcome({ pw: password, status: count > 0 ? 'pwned' : 'safe' }))
        .catch((err: unknown) => {
          // A superseding keystroke aborts the in-flight request — that render's
          // cleanup already ran, so just drop it rather than flashing `error`.
          if (err instanceof DOMException && err.name === 'AbortError') return;
          setOutcome({ pw: password, status: 'error' });
        })
        .finally(() => clearTimeout(timeout));
    }, DEBOUNCE_MS);

    return () => {
      clearTimeout(debounce);
      clearTimeout(timeout);
      controller.abort();
    };
  }, [password, ready]);

  if (!ready) return 'idle';
  if (outcome && outcome.pw === password) return outcome.status;
  return 'checking';
}

/**
 * Whether a given breach-check status should block form submission. Only a
 * confirmed `pwned` blocks — `checking`/`error`/`idle` stay non-blocking so the
 * button never hangs on the network (the server re-checks regardless).
 */
export function pwnedBlocksSubmit(status: PwnedStatus): boolean {
  return status === 'pwned';
}
